/*
 * Copyright (c) 2025. Bluemate B.V.
 */
import './style/subscriptionUsage.scss';

import React, {Fragment, MouseEventHandler, useMemo, useState} from 'react';

import {useTranslation} from 'react-i18next';

import IDate from '@dvrd/idate';

import {AwesomeIcon, Loader} from '@dvrd/dvr-controls';

interface Props {
    onClickPortal: MouseEventHandler;
    currentPeriodStart: IDate;
    currentPeriodEnd: IDate;
    currentOrderCount: number;
    previousPeriods: Record<string, number>;
    loading: boolean;
}

const SHOW_MIN_PERIODS = 2; // Show min 2 previous periods

export default function SubscriptionUsage(props: Props) {
    const {t} = useTranslation();
    const {onClickPortal, currentPeriodStart, currentPeriodEnd, currentOrderCount, previousPeriods, loading} = props;
    const [morePeriodsOpen, setMorePeriodsOpen] = useState(false);
    const hasMorePeriods = useMemo(() => {
        return Object.keys(previousPeriods).length > SHOW_MIN_PERIODS;
    }, [previousPeriods]);

    function onToggleMorePeriods() {
        setMorePeriodsOpen(!morePeriodsOpen);
    }

    function renderMinPreviousPeriods() {
        const keys = Object.keys(previousPeriods).slice(0, SHOW_MIN_PERIODS);
        if (!keys.length) return null;
        return renderPreviousPeriods(keys);
    }

    function renderMorePeriods() {
        if (!morePeriodsOpen || !hasMorePeriods) return null;
        const keys = Object.keys(previousPeriods).slice(SHOW_MIN_PERIODS);
        return renderPreviousPeriods(keys);
    }

    function renderPreviousPeriods(keys: Array<string>) {
        return keys.map((period: string, index: number) => (
            <Fragment key={index}>
                <label>{previousPeriods[period]}</label>
                <label>{period}</label>
                <label />
            </Fragment>
        ));
    }

    return (
        <div className='subscription-block current-usage'>
            <Loader active={loading} />
            <div className='header-container with-icon'>
                <AwesomeIcon name='chart-simple' className='header-icon' />
                <label className='header-title'>
                    {t('settings.subscription.usageThisPeriod', 'Verbruik deze periode')}
                </label>
                <label className='sub-title'>
                    {t('settings.subscription.sinceUntilToday', 'Sinds {{date}} tot vandaag', {
                        date: currentPeriodStart.format('DD-MM-YYYY') ?? '-'
                    })}
                </label>
            </div>
            <div className='body-container'>
                <label className='bold'>{t('settings.subscription.numOrders', 'Aantal orders')}</label>
                <label className='bold'>{t('settings.subscription.thisPeriod', 'Deze periode')}</label>
                <label className='subscription-action' onClick={onClickPortal}>
                    {t('settings.subscription.viewInvoices', 'Facturen bekijken')}
                </label>
                <label>{currentOrderCount}</label>
                <label>
                    {currentPeriodEnd.format('DD-MM-YYYY') ?? '-'} t/m {currentPeriodStart?.format('DD-MM-YYYY') ?? '-'}
                </label>
                <label />
                {renderMinPreviousPeriods()}
                {renderMorePeriods()}
                {hasMorePeriods && (
                    <label className='subscription-action' onClick={onToggleMorePeriods}>
                        {morePeriodsOpen
                            ? t('settings.subscription.showLess', 'Toon minder')
                            : t('settings.subscription.showMore', 'Toon meer')}{' '}
                        {t('settings.subscription.previousPeriods', 'vorige periodes')}
                    </label>
                )}
            </div>
        </div>
    );
}
